<template>
  <div class="default-layout">
    <Navigation
      v-if="$route.meta.navigation !== false"
      :items="items"
      :action="{ name: 'dashboard' }"
      dropdown-trigger="click"
    >
      <template #center v-if="trialPeriodEnd">
        <div class="default-layout__trial-period-end">
          <Badge theme="warning" type="default" icon="fa-pro-light:clock" :value="trialPeriodEnd" />
        </div>
      </template>
    </Navigation>
    <div class="default-layout__content">
      <slot></slot>
    </div>
    <Footer v-if="$route.meta.footer !== false" :navigation="items" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import RootAPI from '@/api/Root'
import moment from 'moment'
import Badge from '@/components/Badge'

export default {
  props: {
    title: {
      type: String,
      default() {
        return this.$t('loading') + '...'
      },
    },
  },

  computed: {
    ...mapState(['businessSite', 'user', 'enums']),
    disabledFeatures() {
      return this.$store.state.enums.DisabledFeatures
    },
    trialPeriodEnd() {
      const end = this.$path('enums.ClientDetails.trialPeriodEnd', this)
      if (!end) return

      var endDate = moment(end).endOf('day')
      var today = moment().startOf('day')
      var diff = Math.ceil(endDate.diff(today, 'days', true))

      if (0 >= diff) return this.$t('trialPeriodExpired')

      return this.$tc('trialPeriodEndsIn', diff === 1 ? 1 : 2, { x: diff })
    },

    items() {
      if (!this.$auth.isAuthenticated)
        return [
          {
            label: this.$t('login'),
            icon: ['fad', 'lock'],
            action: window.location.origin,
          },
        ]
      return [
        {
          label: this.$t('dashboard'),
          exact: true,
          icon: ['fad', 'columns'],
          action: { name: 'dashboard' },
        },
        {
          label: this.$tc('project', 2),
          icon: ['fad', 'drafting-compass'],
          action: { name: 'projects' },
          condition: !this.disabledFeatures.includes('project'),
        },
        ...this.enums.PageCategories.map(({ label, icon, id: category }) => ({
          label: this.$tc(label, 2),
          icon: ['fad', icon],
          action: { name: 'pages', query: { category } },
          exact: true,
        })),
        {
          id: 'business-site',
          label: this.$t('businessSite'),
          icon: ['fad', 'browser'],
          items: [
            ...this.businessPages,
            { class: 'divider' },
            {
              label: this.$t('settings'),
              icon: ['fal', 'cog'],
              action: this.businessSite && this.businessSite.settingsUrl,
              target: '_blank',
            },
          ],
          condition: this.businessSite && this.businessSite.enabled,
        },
        {
          id: 'ads',
          label: this.$t('addon:ads'),
          icon: ['fad', 'bullseye-pointer'],
          action: { name: 'ads' },
          condition: !this.enums.MarketingDisabled,
        },
        {
          id: 'action-menu',
          type: 'list',
          placement: 'menu',
          is: 'ActionsComponent',
        },
        {
          label: this.$t('more'),
          icon: ['fal', 'ellipsis-v'],
          items: [
            {
              label: this.$tc('user', 2),
              icon: ['fal', 'users'],
              action: { name: 'users' },
              condition: this.$store.getters.isAdmin,
            },
            {
              label: this.$tc('contact', 2),
              icon: ['fal', 'address-card'],
              action: { name: 'contacts' },
              condition: this.$store.getters.hasAccess('contacts'),
            },
            {
              label: this.$tc('unitCatalog', 1),
              icon: ['fal', 'puzzle-piece'],
              action: { name: 'residential-templates' },
              condition:
                (this.$store.getters.hasAccess('residentialTemplates') ||
                  this.$store.getters.hasAccess('residentialTemplateLeads')) &&
                !this.disabledFeatures.includes('project'),
            },
            {
              label: this.$tc('product', 2),
              icon: ['fal', 'shopping-cart'],
              action: { name: 'products' },
              condition: this.$store.getters.hasAccess('products') && !this.disabledFeatures.includes('project:shop'),
            },
            {
              label: this.$tc('setting', 2),
              icon: ['fal', 'cog'],
              action: { name: 'settings' },
              condition: this.$store.getters.isAdmin || this.$store.getters.isDeveloper,
            },
            {
              label: this.$tc('complaint', 2),
              icon: ['fal', 'tasks'],
              action: { name: 'complaints' },
              condition:
                ['admin', 'complaints'].some(r => this.user.access.includes(r)) &&
                !this.disabledFeatures.includes('project:complaints'),
            },
            {
              label: this.$t('customerService'),
              condition: Boolean(this.$store.state.intercomAppId),
              icon: ['fal', 'comment'],
              action: () => window.Intercom('show'),
            },
            {
              label: this.$t('helpTools'),
              condition: Boolean(this.$store.state.intercomAppId),
              icon: ['fal', 'question'],
              action: `https://help.kvass.no/${this.$store.state.enums.Language === 'nb' ? 'nb' : 'en'}`,
              target: '_blank',
            },
            {
              label: this.$tc('subscriptionOverview'),
              icon: ['fal', 'cart-shopping'],
              action: { name: 'subscriptions' },
              condition: this.$store.getters.isSuperadmin,
            },
            {
              type: 'list',
              placement: 'more-menu',
              is: 'ActionsComponent',
            },
          ].filter(i => i.condition === undefined || i.condition),
        },
        {
          class: `user-profile`,
          style: {
            backgroundImage: `url(/api/user/${this.$auth.user.email}/avatar)`,
          },
          items: [
            {
              label: this.$t('edit'),
              icon: ['fal', 'user-circle'],
              action: { name: 'user-edit', params: { id: this.user.id } },
            },
            {
              label: this.$t('switchTenant'),
              icon: ['fal', 'sync-alt'],
              condition: this.$path('https://tenants.kvass.no/claims/groups.length', this.$auth.user),
              action: { name: 'tenants' },
            },
            {
              type: 'list',
              placement: 'profile-menu',
              is: 'ActionsComponent',
            },

            {
              label: this.$t('logout'),
              icon: ['fal', 'lock'],
              action: '/api/auth/logout',
            },
          ].filter(i => i.condition === undefined || i.condition),
        },
      ].filter(i => i.condition === undefined || i.condition)
    },
    businessPages() {
      if (!this.businessSite || !this.businessSite.pages || !this.businessSite.pages.length) return []

      let items = this.businessSite.pages
      let root = items.filter(item => item.parent === '0')

      let transform = (item, indent) => ({
        label: item.name,
        action: item.editUrl,
        target: '_blank',
        icon: ['fal', 'pen'],
        class: 'indent--' + indent,
      })

      let handler = (rawItem, indent = 0) => {
        return [
          transform(rawItem, indent),
          ...items.filter(child => child.parent === rawItem.id).map(child => handler(child, indent + 1)),
        ]
      }

      return root.map(item => handler(item, 0)).flat(Infinity)
    },
  },
  created() {
    this.$store.state.initialLoad.promise.then(() => {
      if (!this.$auth.isAuthenticated) return
    })
  },
  components: {
    Navigation,
    Footer,
    Badge,
  },
}
</script>

<style lang="scss">
.default-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__trial-period-end {
    font-size: 1.3rem;
    flex-grow: 1;
    // padding: 0rem 1rem;
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .elder__navigation-node[data-id='action-menu'] {
    margin-left: 0;

    display: flex;
    gap: 0.5rem;

    .action-button {
      font: inherit;
      font-weight: 600;

      border: none;
      border-radius: var(--border-radius);
      background: transparent;
      color: var(--vue-elder-text-color, #444);

      .elder-button__label {
        padding: 0.5rem 1rem;
      }
    }
  }

  .elder__navigation {
    &-logo-image {
      max-height: var(--logo-height) !important;
    }
    &.elder__navigation--responsive .user-profile {
      background-image: none !important;
    }

    .divider {
      padding: 0;
      height: 1px;
      width: 100%;
      background-color: var(--border-color);

      &:hover:before {
        content: none;
      }
    }

    &:not(.elder__navigation--responsive) {
      .user-profile {
        height: 2.5rem;
        aspect-ratio: 1;
        border-radius: 20rem;
        background-color: var(--border-color);
        border: 1px solid #d0d0d0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin-left: 0.5rem;

        background-size: cover;
        background-position: center;

        & > div {
          display: none;
        }
      }
    }
  }
}
</style>
